// M24 — Statistics screen (UI). Pure presentation (ENG-1): the tracker
// (sim/game/stats) holds the counts; this renders the pause-menu Statistics browser
// — a General tab (custom totals) plus one tab per per-id category, each a sorted
// icon+name+count table. Item icons come from the shared ItemIcons cache; entity
// ids (Killed) have no item icon and render as text only.
import {
  CUSTOM_STATS,
  STAT_CATEGORIES,
  type StatCategory,
  type StatsTracker,
} from '../../sim/game/stats';
import type { IconUrl } from './advancements';

/** 'diamond_ore' → 'Diamond Ore'. */
function prettyId(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The Statistics screen (pause-menu). Tabs: General + one per category. */
export function showStats(stats: StatsTracker, iconUrl: IconUrl, onClose: () => void): HTMLElement {
  const screen = document.createElement('div');
  screen.className = 'bb-screen bb-pause bb-adv-screen';

  const panel = document.createElement('div');
  panel.className = 'bb-panel bb-adv-panel';

  const title = document.createElement('div');
  title.className = 'bb-adv-title';
  title.textContent = 'Statistics';

  const tabs = document.createElement('div');
  tabs.className = 'bb-creative-tabs';
  const list = document.createElement('div');
  list.className = 'bb-adv-tree';

  const emptyRow = (text: string): void => {
    const row = document.createElement('div');
    row.className = 'bb-stats-empty';
    row.textContent = text;
    list.append(row);
  };

  const valueRow = (label: string, value: string, icon?: string): void => {
    const row = document.createElement('div');
    row.className = 'bb-stats-row';
    if (icon !== undefined) {
      const ic = document.createElement('div');
      ic.className = 'bb-adv-icon';
      const url = iconUrl(icon);
      if (url) ic.style.backgroundImage = `url(${url})`;
      row.append(ic);
    }
    const name = document.createElement('div');
    name.className = 'bb-stats-name';
    name.textContent = label;
    const val = document.createElement('div');
    val.className = 'bb-stats-val';
    val.textContent = value;
    row.append(name, val);
    list.append(row);
  };

  const renderGeneral = (): void => {
    list.replaceChildren();
    for (const def of CUSTOM_STATS) valueRow(def.label, def.format(stats.getCustom(def.id)));
  };

  const renderCategory = (cat: StatCategory): void => {
    list.replaceChildren();
    const rows = stats.entries(cat);
    if (rows.length === 0) {
      emptyRow('— nothing yet —');
      return;
    }
    for (const [id, count] of rows) valueRow(prettyId(id), count.toLocaleString('en-US'), id);
  };

  const addTab = (label: string, render: () => void): void => {
    const tab = document.createElement('button');
    tab.className = 'bb-tab';
    tab.textContent = label;
    tab.onclick = () => {
      for (const t of tabs.children) t.classList.remove('bb-tab-active');
      tab.classList.add('bb-tab-active');
      render();
    };
    tabs.append(tab);
  };

  addTab('General', renderGeneral);
  for (const c of STAT_CATEGORIES) addTab(c.label, () => renderCategory(c.id));
  (tabs.firstElementChild as HTMLButtonElement | null)?.click();

  const close = document.createElement('button');
  close.className = 'bb-btn';
  close.textContent = 'Close';
  const dismiss = (): void => {
    screen.remove();
    onClose();
  };
  close.onclick = dismiss;
  screen.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') dismiss();
  });

  panel.append(title, tabs, list, close);
  screen.append(panel);
  document.body.append(screen);
  screen.tabIndex = -1;
  screen.focus();
  return screen;
}
